/**
 * The Walliyo mark, in the console (WALLIYO-0137).
 *
 * The shapes come from `@wallyo/contracts/brand`, which the app and the asset
 * generator draw from too, so the mark cannot drift between the three.
 *
 * `tone` picks the colourway by the ground it sits on, not by a theme: the
 * sign-in rail and the header are dark, a printed page is light. Below about
 * 48px pass `detail="simple"` — the tape's ticks and the scissors' pivot turn
 * to mush at that size and dirty the strokes.
 */

import {
  HAT_TILT,
  MARK_BOX,
  MARK_COLOURWAYS,
  bodyParts,
  hatParts,
  holeParts,
  type MarkColourway,
  type MarkDetail,
  type MarkPart,
} from "@wallyo/contracts/brand";
import { useId } from "react";

function Shape({ part, ink, accent }: { part: MarkPart; ink: string; accent: string }) {
  const colour = (tone: "ink" | "accent") => (tone === "ink" ? ink : accent);

  switch (part.shape) {
    case "rect":
      return <rect x={part.x} y={part.y} width={part.width} height={part.height} fill={colour(part.fill)} />;
    case "ellipse":
      return (
        <ellipse
          cx={part.cx}
          cy={part.cy}
          rx={part.rx}
          ry={part.ry}
          transform={`rotate(${part.turn} ${part.cx} ${part.cy})`}
          fill="none"
          stroke={colour(part.stroke)}
          strokeWidth={part.width}
        />
      );
    case "circle":
      return "fill" in part ? (
        <circle cx={part.cx} cy={part.cy} r={part.r} fill={colour(part.fill)} />
      ) : (
        <circle
          cx={part.cx}
          cy={part.cy}
          r={part.r}
          fill="none"
          stroke={colour(part.stroke)}
          strokeWidth={part.width}
        />
      );
    default:
      return "fill" in part ? (
        <path d={part.d} fill={colour(part.fill)} />
      ) : (
        <path
          d={part.d}
          fill="none"
          stroke={colour(part.stroke)}
          strokeWidth={part.width}
          strokeLinecap={part.round ? "round" : undefined}
          strokeLinejoin={part.round ? "round" : undefined}
        />
      );
  }
}

export function BrandMark({
  size = 48,
  tone = "onDark",
  detail,
  title,
}: {
  size?: number;
  tone?: MarkColourway;
  detail?: MarkDetail;
  /** Given a title the mark is an image with a name; without one it is decoration. */
  title?: string;
}) {
  // Two marks on one page would otherwise share a mask id and cut each other.
  const maskId = `mark-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const level: MarkDetail = detail ?? (size < 48 ? "simple" : "full");
  const { ink, accent } = MARK_COLOURWAYS[tone];
  const holes = holeParts(level);
  const tilt = `rotate(${HAT_TILT.angle} ${HAT_TILT.x} ${HAT_TILT.y})`;

  // A hole is the same shape drawn again in black over the mask's white, so
  // whatever is behind the mark shows through it.
  const hole = (part: MarkPart, key: number) => <Shape key={key} part={part} ink="#000" accent="#000" />;

  return (
    <svg
      width={size}
      height={Math.round((size * MARK_BOX.height) / MARK_BOX.width)}
      viewBox={`${MARK_BOX.x} ${MARK_BOX.y} ${MARK_BOX.width} ${MARK_BOX.height}`}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={MARK_BOX.x}
          y={MARK_BOX.y}
          width={MARK_BOX.width}
          height={MARK_BOX.height}
        >
          <rect x={MARK_BOX.x} y={MARK_BOX.y} width={MARK_BOX.width} height={MARK_BOX.height} fill="#fff" />
          {holes.tilted.length > 0 ? <g transform={tilt}>{holes.tilted.map(hole)}</g> : null}
          {holes.flat.map(hole)}
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <g transform={tilt}>
          {hatParts(level).map((part, index) => (
            <Shape key={index} part={part} ink={ink} accent={accent} />
          ))}
        </g>
        {bodyParts(level).map((part, index) => (
          <Shape key={index} part={part} ink={ink} accent={accent} />
        ))}
      </g>
    </svg>
  );
}
